<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
/* global axios */

// 每日留电面板(2026-09-09, 自树莓派监控屏移植): 按归属者统计 今日/昨日 留电与对话,
// 附留电比与月度汇总。所有人可见(不限于管理员), 数据与留电来源报表同口径。
// 后端 GET /api/v1/accounts/:id/contacts/lead_source_report (since/until 北京时区)
// 今日/昨日各拉一次; 60s 自动刷新, 与监控屏同节奏。

const route = useRoute();
const accountId = computed(() => route.params.accountId);

const loading = ref(false);
const loaded = ref(false);
const agents = ref([]); // [{name, today, yesterday, convToday, convYesterday, domains}]
const totals = ref({
  today: 0,
  yesterday: 0,
  convToday: 0,
  convYesterday: 0,
  monthThis: 0,
  monthLast: 0,
  monthLabel: '',
  prevMonthLabel: '',
});

// 域名简写(与树莓派监控屏一致): widget 嵌入页域名 → 站群代号
const DOMAIN_ABBR = {
  'a.bjhgyjs.cn': 'A',
  'www.bjhgyjs.cn': 'CN',
  'www.yjssi.com': '4',
  'www.bjjcyjs.com': 'BS',
  'www.yjsshi.com': '10',
};

// 北京时区"今天"(UTC 毫秒 +8h 后按 wall-clock 读)
const bjNow = () => new Date(Date.now() + 8 * 3600000);
const bjDateStr = d =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

// rows(归属者×来源) 按归属者求和
const sumByOwner = rows => {
  const out = {};
  (rows || []).forEach(r => {
    const owner = r.owner || '未分配';
    out[owner] = (out[owner] || 0) + (r.count || 0);
  });
  return out;
};
const convByOwner = rows => {
  const out = {};
  (rows || []).forEach(r => {
    out[r.owner || '未分配'] = r.count || 0;
  });
  return out;
};

const fetchPanel = async () => {
  loading.value = true;
  try {
    const today = bjDateStr(bjNow());
    const yesterday = bjDateStr(new Date(bjNow().getTime() - 86400000));
    const [tRes, yRes] = await Promise.all([
      axios.get(`/api/v1/accounts/${accountId.value}/contacts/lead_source_report`, {
        params: { since: today, until: today },
      }),
      axios.get(`/api/v1/accounts/${accountId.value}/contacts/lead_source_report`, {
        params: { since: yesterday, until: yesterday },
      }),
    ]);
    const t = tRes.data;
    const y = yRes.data;

    const tLeads = sumByOwner(t.rows);
    const yLeads = sumByOwner(y.rows);
    const tConvs = convByOwner(t.conversations);
    const yConvs = convByOwner(y.conversations);

    // 今日域名留电标签(domains = 归属者×widget 所在域名, 已按今日日期过滤)
    const tDomains = {};
    (t.domains || []).forEach(r => {
      const abbr = DOMAIN_ABBR[String(r.domain || '').toLowerCase()]
        || String(r.domain || '').replace(/^www\./, '');
      if (!abbr) return;
      tDomains[r.owner || '未分配'] = tDomains[r.owner || '未分配'] || {};
      tDomains[r.owner || '未分配'][abbr] = (tDomains[r.owner || '未分配'][abbr] || 0) + (r.count || 0);
    });

    const names = [...new Set([...Object.keys(tLeads), ...Object.keys(yLeads)])];
    agents.value = names
      .map(name => ({
        name,
        today: tLeads[name] || 0,
        yesterday: yLeads[name] || 0,
        convToday: tConvs[name] || 0,
        convYesterday: yConvs[name] || 0,
        domains: Object.entries(tDomains[name] || {})
          .sort((a, b) => b[1] - a[1])
          .map(([k, n]) => ({ k, n })),
      }))
      .sort((a, b) => b.today - a.today || b.yesterday - a.yesterday);

    totals.value = {
      today: t.total || 0,
      yesterday: y.total || 0,
      convToday: t.conversations_total || 0,
      convYesterday: y.conversations_total || 0,
      monthThis: t.month_total || 0,
      monthLast: t.prev_month_total || 0,
      monthLabel: t.month_label || '本月',
      prevMonthLabel: t.prev_month_label || '上月',
    };
    loaded.value = true;
  } catch {
    useAlert('每日留电加载失败', 'error');
  } finally {
    loading.value = false;
  }
};

// 每人留电比 = 今日留电 ÷ 今日对话(对话 0 显示 —)
const ratio = it => (it.convToday > 0 ? `${Math.round((it.today / it.convToday) * 100)}%` : '—');

// 今日 vs 昨日 涨跌文案
const fmtDelta = (cur, prev) => {
  const diff = cur - prev;
  if (diff === 0) return '持平';
  const pct = prev > 0 ? `（${diff > 0 ? '+' : ''}${Math.round((diff / prev) * 100)}%）` : '';
  return `${diff > 0 ? '+' : ''}${diff}${pct}`;
};

const overallRatio = computed(() =>
  totals.value.convToday > 0
    ? `${Math.round((totals.value.today / totals.value.convToday) * 100)}%`
    : '—'
);

// 60s 自动刷新(与监控屏同节奏); 离开页面清掉
let timer = null;
onMounted(() => {
  fetchPanel();
  timer = setInterval(fetchPanel, 60000);
});
onUnmounted(() => clearInterval(timer));
</script>

<template>
  <div class="overflow-auto bg-n-surface-1 w-full px-6">
    <div class="max-w-5xl mx-auto py-6 space-y-4">
      <div class="flex items-end justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">每日留电</h1>
          <p class="text-sm text-n-slate-10 mt-0.5">
            按归属者统计今日/昨日留电与对话(北京时区), 每 60 秒自动刷新
          </p>
        </div>
        <span
          v-if="loading"
          class="text-xs text-n-slate-10 pb-1.5 shrink-0"
        >查询中…</span>
      </div>

      <div
        v-if="!loaded"
        class="bg-n-solid-3 rounded-xl py-24 text-center text-n-slate-10"
      >
        加载中...
      </div>

      <template v-else>
        <!-- 汇总卡 -->
        <div
          class="grid grid-cols-2 sm:grid-cols-4 gap-3 transition-opacity"
          :class="loading ? 'opacity-50' : ''"
        >
          <div class="bg-n-blue-4/20 rounded-xl p-4 border border-n-blue-5/30">
            <div class="text-xs text-n-blue-9 mb-1">今日留电</div>
            <div class="text-2xl font-semibold text-n-blue-11">
              {{ totals.today.toLocaleString() }}
            </div>
            <div class="text-xs text-n-slate-10 mt-0.5">
              昨日 {{ totals.yesterday.toLocaleString() }} · {{ fmtDelta(totals.today, totals.yesterday) }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">今日对话</div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ totals.convToday.toLocaleString() }}
            </div>
            <div class="text-xs text-n-slate-10 mt-0.5">
              昨日 {{ totals.convYesterday.toLocaleString() }} · {{ fmtDelta(totals.convToday, totals.convYesterday) }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">{{ totals.monthLabel }}留电</div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ totals.monthThis.toLocaleString() }}
            </div>
            <div class="text-xs text-n-slate-10 mt-0.5">
              {{ totals.prevMonthLabel }} {{ totals.monthLast.toLocaleString() }}
            </div>
          </div>
          <div class="bg-n-solid-3 rounded-xl p-4">
            <div class="text-xs text-n-slate-10 mb-1">今日留电比</div>
            <div class="text-2xl font-semibold text-n-slate-12">{{ overallRatio }}</div>
            <div class="text-xs text-n-slate-10 mt-0.5">今日留电 ÷ 今日对话</div>
          </div>
        </div>

        <!-- 每人明细(与监控屏同列: 今日/昨日留电 + 今日/昨日对话 + 留电比) -->
        <div :class="loading ? 'opacity-50' : ''" class="transition-opacity">
          <div class="bg-n-solid-3 rounded-xl overflow-hidden">
            <div
              v-if="!agents.length"
              class="py-16 text-center text-n-slate-10"
            >
              今日与昨日均无留电数据
            </div>
            <table v-else class="w-full text-sm">
              <thead>
                <tr class="bg-n-slate-2 border-b border-n-slate-4 text-n-slate-11">
                  <th class="px-4 py-2.5 text-left font-medium whitespace-nowrap">归属者</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">今日留电</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">昨日留电</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">今日对话</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">昨日对话</th>
                  <th class="px-4 py-2.5 text-right font-medium whitespace-nowrap">留电比</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="it in agents"
                  :key="it.name"
                  class="border-b border-n-slate-3 hover:bg-n-brand/5"
                >
                  <!-- 修复(2026-09-11): 域名标签原与名字同行(inline)把首列撑宽,
                       站点多时右侧数字列被挤出屏幕。改为名字一行、域名标签独立成组
                       换行(flex-wrap), 数字列始终可见。 -->
                  <td class="px-4 py-2 text-n-slate-12">
                    <div class="whitespace-nowrap">{{ it.name }}</div>
                    <div
                      v-if="it.domains.length"
                      class="mt-1 flex flex-wrap gap-1"
                    >
                      <span
                        v-for="d in it.domains"
                        :key="d.k"
                        class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-n-slate-3 text-n-slate-11"
                        :title="`今日 ${d.k} 站留电 ${d.n} 条`"
                      >{{ d.k }}<b class="font-semibold">{{ d.n }}</b></span>
                    </div>
                  </td>
                  <td class="px-4 py-2 text-right font-semibold text-n-slate-12 tabular-nums">
                    {{ it.today.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2 text-right text-n-slate-11 tabular-nums">
                    {{ it.yesterday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2 text-right text-n-slate-12 tabular-nums">
                    {{ it.convToday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2 text-right text-n-slate-11 tabular-nums">
                    {{ it.convYesterday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2 text-right font-semibold text-n-slate-12 tabular-nums">
                    {{ ratio(it) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="bg-n-slate-2 border-t-2 border-n-slate-5 font-semibold">
                  <td class="px-4 py-2.5 text-n-slate-12">合计</td>
                  <td class="px-4 py-2.5 text-right text-n-slate-12 tabular-nums">
                    {{ totals.today.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2.5 text-right text-n-slate-11 tabular-nums">
                    {{ totals.yesterday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2.5 text-right text-n-slate-12 tabular-nums">
                    {{ totals.convToday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2.5 text-right text-n-slate-11 tabular-nums">
                    {{ totals.convYesterday.toLocaleString() }}
                  </td>
                  <td class="px-4 py-2.5 text-right text-n-slate-12 tabular-nums">
                    {{ overallRatio }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
